import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CorporateAccountOpenProfileDetailsPages, useCorporateAccountOpenProfileDetailsPageTypeStore } from 'views/pages/accountOpenProfileDetails/corporateProfileDetails/hooks';
import { useEffect } from 'react';
import { useCorporateAccountEstablishStore } from '../hooks';
import { ErrorWord, getMaxLengthErrorMessage, TemplateProps, yapNumberWhen, yapStringWhen, yupNumberString } from '../common';
import { useGetDateTimeQuery } from 'views/utils/graphql/graphql';
import { CorporateBusinessContent, } from 'views/utils/enums';
import { AddressInfo } from 'views/templates/module/addressInput/hook';
import { validateCorporateNumber } from 'views/utils/corporateNumberCheckDigit';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';

export enum CorporateAddressConfirmSelectType {
  None = 0,
  Same = 1,
  Different = 2,
}

export type CorporateInfoType = {
  corporateNumber: string;
  corporateName: string;
  corporateFurigana: string;
  corporatePostCode: string;
  corporatePrefecture: string;
  corporateCityCountry: string;
  corporateTownStreetAddress: string;
  hasNoCorporateTownStreetAddress: boolean; // リクエストに不要
  corporateBuildingNameAndRoomNumber: string;
  corporateAddressConfirm: CorporateAddressConfirmSelectType;
  registeredCorporatePostCode: string;
  registeredCorporatePrefecture: string;
  registeredCorporateCityCountry: string;
  registeredCorporateTownStreetAddress: string;
  hasNoRegisteredCorporateTownStreetAddress: boolean; // リクエストに不要
  registeredCorporateBuildingNameAndRoomNumber: string;
  corporateSettlementMonth: string;
  corporateSettlementDay: string;
  corporateEstablishmentDate: Date | null;
  corporateMainBusinessContent: string;
  corporateMainBusinessContentOtherText: string;
  corporateOtherBusinessContent: string;
  corporateOtherBusinessContentOtherText: string;
  corporateCapital: string;
  corporateNumberOfEmployees: string;
  corporateHomepage: string;
  electronicDocumentAgreement: boolean;
  electronicDocumentAgreementDateTime: string;
};

export const useCorporateInfo = ({ isUpdate }: TemplateProps) => {
  const { param, add } = useCorporateAccountEstablishStore();
  const { setAccountOpenProfileDetailsPageType } = useCorporateAccountOpenProfileDetailsPageTypeStore();
  const [dateTime, getDataTime] = useGetDateTimeQuery({
    requestPolicy: 'network-only',
  });
  const { messageClose } = useGlobalToastHandler();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validation = Yup.object().shape({
    corporateNumber: Yup.string().test({
      name: 'corporateNumber',
      test(value, ctx) {
        const message = validateCorporateNumber(value);
        if (message) {
          return ctx.createError({ message });
        }

        return true;
      },
    }),
    corporateName: Yup.string().required(ErrorWord.INPUT).max(80, getMaxLengthErrorMessage(80)),
    corporateFurigana: Yup.string().required(ErrorWord.INPUT).matches(/^[ァ-ヴー_]+$/, ErrorWord.FURIGANA_KATAKANA).max(255, getMaxLengthErrorMessage(255)),
    corporatePostCode: yupNumberString({ length: 7, isRequired: true, errorWord: ErrorWord.POSTCODE }),
    corporatePrefecture: Yup.string().required(ErrorWord.SELECT),
    corporateCityCountry: Yup.string().required(ErrorWord.INPUT).max(40, getMaxLengthErrorMessage(40)),
    corporateTownStreetAddress: yapStringWhen('hasNoCorporateTownStreetAddress', false, ErrorWord.INPUT).max(255, getMaxLengthErrorMessage(255)),
    corporateBuildingNameAndRoomNumber: Yup.string().max(50, getMaxLengthErrorMessage(50)),
    corporateAddressConfirm: Yup.number().not([CorporateAddressConfirmSelectType.None], ErrorWord.SELECT),
    registeredCorporatePostCode: yapNumberWhen(
      'corporateAddressConfirm',
      (val: CorporateAddressConfirmSelectType) => val === CorporateAddressConfirmSelectType.Different,
      ErrorWord.POSTCODE,
      7,
      false,
    ),
    registeredCorporatePrefecture: yapStringWhen(
      'corporateAddressConfirm',
      (val: CorporateAddressConfirmSelectType) => val === CorporateAddressConfirmSelectType.Different,
      ErrorWord.SELECT,
    ),
    registeredCorporateCityCountry: yapStringWhen(
      'corporateAddressConfirm',
      (val: CorporateAddressConfirmSelectType) => val === CorporateAddressConfirmSelectType.Different,
      ErrorWord.INPUT,
    ).max(255, getMaxLengthErrorMessage(255)),
    registeredCorporateTownStreetAddress: yapStringWhen(
      ['corporateAddressConfirm', 'hasNoRegisteredCorporateTownStreetAddress'],
      (confirm, hasNo) => confirm === CorporateAddressConfirmSelectType.Different && !hasNo,
      ErrorWord.INPUT,
    ).max(255, getMaxLengthErrorMessage(255)),
    registeredCorporateBuildingNameAndRoomNumber: Yup.string().max(255, getMaxLengthErrorMessage(255)),
    corporateSettlementMonth: Yup.string().required(ErrorWord.SELECT),
    corporateSettlementDay: Yup.string().required(ErrorWord.SELECT),
    corporateEstablishmentDate: Yup.date().required(ErrorWord.Date),
    corporateMainBusinessContent: Yup.string().required(ErrorWord.SELECT),
    corporateMainBusinessContentOtherText: yapStringWhen(
      'corporateMainBusinessContent',
      (val: string) => val === CorporateBusinessContent.Sonohoka,
      ErrorWord.INPUT,
    ).max(25, getMaxLengthErrorMessage(25)),
    corporateOtherBusinessContentOtherText: yapStringWhen(
      'corporateOtherBusinessContent',
      (val: string) => val === CorporateBusinessContent.Sonohoka,
      ErrorWord.INPUT,
    ).max(25, getMaxLengthErrorMessage(25)),
    corporateCapital: Yup.number()
      .typeError(ErrorWord.CAPITAL)
      .min(1, ErrorWord.CAPITAL)
      .max(Math.pow(10, 11) - 1, ErrorWord.CAPITAL)
      .integer(ErrorWord.CAPITAL)
      .required(ErrorWord.CAPITAL),
    corporateNumberOfEmployees: Yup.number()
      .typeError(ErrorWord.EMPLOYEE)
      .min(1, ErrorWord.EMPLOYEE)
      .max(Math.pow(10, 5) - 1, ErrorWord.EMPLOYEE)
      .integer(ErrorWord.EMPLOYEE)
      .required(ErrorWord.EMPLOYEE),
    corporateHomepage: Yup.string().matches(
      /^(https?)(:\/\/[-_.!~*\'()a-zA-Z0-9;\/?:\@&=+\$,%#]+)$/,
      ErrorWord.HOMEPAGE,
    ).max(255, getMaxLengthErrorMessage(255)),
    electronicDocumentAgreement: Yup.bool().oneOf([true], ErrorWord.UABLE_OPEN_ACCOUNT),
  });

  const initialValues: CorporateInfoType = {
    corporateNumber: param.corporateNumber ?? '',
    corporateName: param.corporateName ?? '',
    corporateFurigana: param.corporateFurigana ?? '',
    corporatePostCode: param.corporatePostCode ?? '',
    corporatePrefecture: param.corporatePrefecture ?? '',
    corporateCityCountry: param.corporateCityCountry ?? '',
    corporateTownStreetAddress: param.corporateTownStreetAddress ?? '',
    hasNoCorporateTownStreetAddress: param.hasNoCorporateTownStreetAddress ?? false,
    corporateBuildingNameAndRoomNumber: param.corporateBuildingNameAndRoomNumber ?? '',
    corporateAddressConfirm: param.corporateAddressConfirm ?? CorporateAddressConfirmSelectType.None,
    registeredCorporatePostCode: param.registeredCorporatePostCode ?? '',
    registeredCorporatePrefecture: param.registeredCorporatePrefecture ?? '',
    registeredCorporateCityCountry: param.registeredCorporateCityCountry ?? '',
    registeredCorporateTownStreetAddress: param.registeredCorporateTownStreetAddress ?? '',
    hasNoRegisteredCorporateTownStreetAddress: param.hasNoRegisteredCorporateTownStreetAddress,
    registeredCorporateBuildingNameAndRoomNumber: param.registeredCorporateBuildingNameAndRoomNumber ?? '',
    corporateSettlementMonth: param.corporateSettlementMonth ?? '',
    corporateSettlementDay: param.corporateSettlementDay ?? '',
    corporateEstablishmentDate: param.corporateEstablishmentDate,
    corporateMainBusinessContent: param.corporateMainBusinessContent ?? '',
    corporateMainBusinessContentOtherText: param.corporateMainBusinessContentOtherText ?? '',
    corporateOtherBusinessContent: param.corporateOtherBusinessContent ?? '',
    corporateOtherBusinessContentOtherText: param.corporateOtherBusinessContentOtherText ?? '',
    corporateCapital: param.corporateCapital ?? '',
    corporateNumberOfEmployees: param.corporateNumberOfEmployees ?? '',
    corporateHomepage: param.corporateHomepage ?? '',
    electronicDocumentAgreement: param.electronicDocumentAgreement ?? false,
    electronicDocumentAgreementDateTime: param.electronicDocumentAgreementDateTime ?? '',
  };

  const formik = useFormik<CorporateInfoType>({
    enableReinitialize: isUpdate,
    initialValues,
    async onSubmit() {
      add({
        ...param,
        ...formik.values,
      });
      setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.transactionPurpose);
    },
    validationSchema: validation,
    validateOnChange: false,
    validateOnBlur: false,
    validate: () => {
      // バリデーションかける時に必ずエラートーストを閉じる
      messageClose();
    },
  });

  useEffect(() => {
    if (formik.values.electronicDocumentAgreement) {
      getDataTime();
    }
  }, [formik.values.electronicDocumentAgreement]);

  useEffect(() => {
    if (!dateTime.data?.GetDateTime) return;
    formik.values.electronicDocumentAgreementDateTime = dateTime.data?.GetDateTime?.Current!;
  }, [dateTime]);

  const corporateNumberProps = {
    value: formik.values.corporateNumber,
    onChange: (value: string) => formik.setFieldValue('corporateNumber', value),
    error: formik.errors.corporateNumber,
  };
  const corporateNameProps = {
    value: formik.values.corporateName,
    onChange: (value: string) => formik.setFieldValue('corporateName', value),
    error: formik.errors.corporateName,
  };

  const corporateFuriganaProps ={
    value: formik.values.corporateFurigana,
    onChange: (value: string) => formik.setFieldValue('corporateFurigana', value),
    error: formik.errors.corporateFurigana,
  }

  const corporateAddressPorps = {
    postCode: {
      value: formik.values.corporatePostCode,
      onChange: (value: string) => {
        formik.setFieldValue('corporatePostCode', value);
      },
      error: formik.errors.corporatePostCode,
    },
    prefecture: {
      selectedOption: formik.values.corporatePrefecture,
      setSelectedOption: (value: string) => formik.setFieldValue('corporatePrefecture', value),
      error: formik.errors.corporatePrefecture,
    },
    municipality: {
      value: formik.values.corporateCityCountry,
      onChange: (value: string) => {
        formik.setFieldValue('corporateCityCountry', value);
      },
      error: formik.errors.corporateCityCountry,
    },
    streetAddress: {
      value: formik.values.corporateTownStreetAddress,
      onChange: (value: string) => {
        formik.setFieldValue('corporateTownStreetAddress', value);
      },
      error: formik.errors.corporateTownStreetAddress,
      disabled: formik.values.hasNoCorporateTownStreetAddress,
    },
    hasNoTownStreetAddress: {
      checked: formik.values.hasNoCorporateTownStreetAddress,
      setChecked: (value: boolean) => {
        formik.setFieldValue('corporateTownStreetAddress', '');
        formik.setFieldValue('hasNoCorporateTownStreetAddress', value);
      },
    },
    buildingNameAndRoomNumber: {
      value: formik.values.corporateBuildingNameAndRoomNumber,
      onChange: (value: string) => formik.setFieldValue('corporateBuildingNameAndRoomNumber', value),
      error: formik.errors.corporateBuildingNameAndRoomNumber,
    },
    onSearchAddress: (addressInfo: AddressInfo) => {
      formik.setFieldValue('corporatePrefecture', addressInfo.prefecture);
      formik.setFieldValue('corporateCityCountry', addressInfo.cityCountry);
      formik.setFieldValue('hasNoCorporateTownStreetAddress', false);
      formik.setFieldValue('corporateTownStreetAddress', addressInfo.townStreetAddress);
      formik.setFieldValue('corporateBuildingNameAndRoomNumber', '');
    },
  };

  const corporateAddressConfirmProps = {
    selected: formik.values.corporateAddressConfirm,
    onChange: (value: CorporateAddressConfirmSelectType) => {
      if (value === CorporateAddressConfirmSelectType.Same) {
        formik.setFieldValue('registeredCorporatePostCode', '');
        formik.setFieldValue('registeredCorporatePrefecture', '');
        formik.setFieldValue('registeredCorporateCityCountry', '');
        formik.setFieldValue('hasNoRegisteredCorporateTownStreetAddress', false);
        formik.setFieldValue('registeredCorporateTownStreetAddress', '');
        formik.setFieldValue('registeredCorporateBuildingNameAndRoomNumber', '');
      }
      formik.setFieldValue('corporateAddressConfirm', value);
    },
    error: formik.errors.corporateAddressConfirm,
  };

  const registeredCorporateAddressPorps = {
    postCode: {
      value: formik.values.registeredCorporatePostCode,
      onChange: (value: string) => {
        formik.setFieldValue('registeredCorporatePostCode', value);
      },
      error: formik.errors.registeredCorporatePostCode,
    },
    prefecture: {
      selectedOption: formik.values.registeredCorporatePrefecture,
      setSelectedOption: (value: string) => formik.setFieldValue('registeredCorporatePrefecture', value),
      error: formik.errors.registeredCorporatePrefecture,
    },
    municipality: {
      value: formik.values.registeredCorporateCityCountry,
      onChange: (value: string) => {
        formik.setFieldValue('registeredCorporateCityCountry', value);
      },
      error: formik.errors.registeredCorporateCityCountry,
    },
    streetAddress: {
      value: formik.values.registeredCorporateTownStreetAddress,
      onChange: (value: string) => {
        formik.setFieldValue('registeredCorporateTownStreetAddress', value);
      },
      error: formik.errors.registeredCorporateTownStreetAddress,
      disabled: formik.values.hasNoRegisteredCorporateTownStreetAddress,
    },
    hasNoTownStreetAddress: {
      checked: formik.values.hasNoRegisteredCorporateTownStreetAddress,
      setChecked: (value: boolean) => {
        formik.setFieldValue('registeredCorporateTownStreetAddress', '');
        formik.setFieldValue('hasNoRegisteredCorporateTownStreetAddress', value);
      },
    },
    buildingNameAndRoomNumber: {
      value: formik.values.registeredCorporateBuildingNameAndRoomNumber,
      onChange: (value: string) => formik.setFieldValue('registeredCorporateBuildingNameAndRoomNumber', value),
      error: formik.errors.registeredCorporateBuildingNameAndRoomNumber,
    },
    onSearchAddress: (addressInfo: AddressInfo) => {
      formik.setFieldValue('registeredCorporatePrefecture', addressInfo.prefecture);
      formik.setFieldValue('registeredCorporateCityCountry', addressInfo.cityCountry);
      formik.setFieldValue('hasNoRegisteredCorporateTownStreetAddress', false);
      formik.setFieldValue('registeredCorporateTownStreetAddress', addressInfo.townStreetAddress);
      formik.setFieldValue('registeredCorporateBuildingNameAndRoomNumber', '');
    },
  };

  const corporateSettlementMonthProps = {
    selectedOption: formik.values.corporateSettlementMonth,
    setSelectedOption: (value: string) => formik.setFieldValue('corporateSettlementMonth', value),
    error: formik.errors.corporateSettlementMonth,
  };

  const corporateSettlementDayProps = {
    selectedOption: formik.values.corporateSettlementDay,
    setSelectedOption: (value: string) => formik.setFieldValue('corporateSettlementDay', value),
    error: formik.errors.corporateSettlementDay,
  };

  const corporateEstablishmentDateProps = {
    date: formik.values.corporateEstablishmentDate,
    name: 'corporateEstablishmentDate',
    minDate: new Date('1700/1/1'),
    maxDate: new Date(),
    onChange: (value: any) => formik.setFieldValue('corporateEstablishmentDate', value),
    onBlur: formik.handleBlur,
    isWeekEndDisable: false,
    error: formik.errors.corporateEstablishmentDate,
  };

  const corporateMainBusinessContentProps = {
    selectedOption: formik.values.corporateMainBusinessContent,
    setSelectedOption: (value: string) => {
      if (value !== CorporateBusinessContent.Sonohoka) {
        formik.setFieldValue('corporateMainBusinessContentOtherText', '');
      }
      formik.setFieldValue('corporateMainBusinessContent', value);
    },
    error: formik.errors.corporateMainBusinessContent,
    otherText: formik.values.corporateMainBusinessContent === CorporateBusinessContent.Sonohoka && {
      value: formik.values.corporateMainBusinessContentOtherText,
      onChange: (value: string) => formik.setFieldValue('corporateMainBusinessContentOtherText', value),
      error: formik.errors.corporateMainBusinessContentOtherText,
    },
  };

  const corporateCapitalProps = {
    value: formik.values.corporateCapital,
    onChange: (value: string) => formik.setFieldValue('corporateCapital', value),
    error: formik.errors.corporateCapital,
  };

  const corporateNumberOfEmployeesProps = {
    value: formik.values.corporateNumberOfEmployees,
    onChange: (value: string) => formik.setFieldValue('corporateNumberOfEmployees', value),
    error: formik.errors.corporateNumberOfEmployees,
  };

  const corporateHomepageProps = {
    value: formik.values.corporateHomepage,
    onChange: (value: string) => formik.setFieldValue('corporateHomepage', value),
    error: formik.errors.corporateHomepage,
  };

  const electronicDocumentAgreementProps = {
    checked: formik.values.electronicDocumentAgreement,
    setChecked: (value: boolean) => formik.setFieldValue('electronicDocumentAgreement', value),
    error: formik.errors.electronicDocumentAgreement,
  };

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    formik,
    corporateNumberProps,
    corporateNameProps,
    corporateFuriganaProps,
    corporateAddressPorps,
    corporateAddressConfirmProps,
    registeredCorporateAddressPorps,
    corporateMainBusinessContentProps,
    corporateSettlementMonthProps,
    corporateSettlementDayProps,
    corporateEstablishmentDateProps,
    corporateCapitalProps,
    corporateNumberOfEmployeesProps,
    corporateHomepageProps,
    electronicDocumentAgreementProps,
  };
};
